/** QuickPlan — Résolution des composants paramétriques et géométrie de placement.
 *
 * resolveComponent(comp, params) évalue toutes les expressions : primitives en
 * nombres, ports expansés, boîte englobante locale, désignation de nomenclature.
 *
 * Conventions de rotation : degrés, ordre « XYZ » de three.js (R = Rx·Ry·Rz) — la
 * vue 3D et le calcul serveur (IA, ports, encombrements) tombent donc juste.
 */
import { evalExpr, evalVec, fillTemplate } from './expr.js';
import { resolveSection, sectionBounds } from './sections.js';

const DEG = Math.PI / 180;

// ── Algèbre 3×3 ─────────────────────────────────────────────────
export function rotMatrix(rot = [0, 0, 0]) {
  const [a, b, c] = rot.map((d) => d * DEG);
  const ca = Math.cos(a), sa = Math.sin(a);
  const cb = Math.cos(b), sb = Math.sin(b);
  const cc = Math.cos(c), sc = Math.sin(c);
  // Rx·Ry·Rz (Euler XYZ intrinsèque de three.js)
  return [
    [cb * cc, -cb * sc, sb],
    [ca * sc + sa * sb * cc, ca * cc - sa * sb * sc, -sa * cb],
    [sa * sc - ca * sb * cc, sa * cc + ca * sb * sc, ca * cb],
  ];
}
export function apply(m, v) {
  return [
    m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
    m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
    m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2],
  ];
}
export const add = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
function normalize(v) {
  const n = Math.hypot(...v);
  return n > 1e-9 ? v.map((x) => x / n) : [0, 0, 1];
}
export const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
const round = (v, q = 1000) => Math.round(v * q) / q;

/** Passage section (sx, sy) + abscisse t → repère local, selon l'axe d'extrusion.
 *  Permutations circulaires (déterminant +1 : pas de face retournée en 3D). */
export function extrudeMap(axis, sx, sy, t) {
  if (axis === 'x') return [t, sx, sy];
  if (axis === 'y') return [sy, t, sx];
  return [sx, sy, t];
}

/** Paramètres effectifs : défauts + valeurs fournies, bornées à [min, max]. */
export function effectiveParams(comp, given = {}) {
  const out = {};
  for (const p of comp.params ?? []) {
    let v = given[p.key];
    if (typeof v !== 'number' || !Number.isFinite(v)) v = p.default;
    if (p.min != null && v < p.min) v = p.min;
    if (p.max != null && v > p.max) v = p.max;
    out[p.key] = v;
  }
  return out;
}

function expand(list, vars, fn) {
  const out = [];
  for (const item of list ?? []) {
    if (item.repeat) {
      const n = Math.max(0, Math.min(1000, Math.floor(evalExpr(item.repeat.count, vars))));
      const key = item.repeat.var ?? 'i';
      for (let i = 0; i < n; i++) out.push(fn(item, { ...vars, [key]: i }));
    } else {
      out.push(fn(item, vars));
    }
  }
  return out;
}

/** Boîte locale d'une primitive résolue, avant sa rotation propre (relative à pos). */
export function partLocalBox(p) {
  if (p.type === 'box') return [[0, 0, 0], p.size];
  if (p.type === 'cyl') {
    const { r, h } = p;
    if (p.axis === 'x') return [[0, -r, -r], [h, r, r]];
    if (p.axis === 'y') return [[-r, 0, -r], [r, h, r]];
    return [[-r, -r, 0], [r, r, h]];
  }
  const b = p.bounds;
  const lo = extrudeMap(p.axis, b.minX, b.minY, 0);
  const hi = extrudeMap(p.axis, b.maxX, b.maxY, p.length);
  return [lo, hi];
}

function corners([lo, hi]) {
  const out = [];
  for (const x of [lo[0], hi[0]]) for (const y of [lo[1], hi[1]]) for (const z of [lo[2], hi[2]]) out.push([x, y, z]);
  return out;
}

function boundsOf(points) {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (const p of points) {
    for (let k = 0; k < 3; k++) {
      if (p[k] < min[k]) min[k] = p[k];
      if (p[k] > max[k]) max[k] = p[k];
    }
  }
  return { min: min.map((v) => round(v)), max: max.map((v) => round(v)) };
}

/** Coins d'une primitive résolue dans le repère du composant. */
export function partCorners(p) {
  const m = p.rot ? rotMatrix(p.rot) : null;
  return corners(partLocalBox(p)).map((c) => add(m ? apply(m, c) : c, p.pos));
}

/** Résout un composant. Lève une erreur explicite si une expression est fausse. */
export function resolveComponent(comp, given = {}) {
  const params = effectiveParams(comp, given);
  const vars = { ...params };

  const parts = expand(comp.parts, vars, (p, v) => {
    const base = {
      type: p.type,
      name: p.name,
      color: p.color,
      opacity: p.opacity,
      pos: evalVec(p.pos, v),
      rot: p.rot ? evalVec(p.rot, v) : null,
    };
    if (p.type === 'box') {
      base.size = evalVec(p.size, v).map((s) => Math.max(0.01, s));
    } else if (p.type === 'cyl') {
      base.r = Math.max(0.01, evalExpr(p.r, v));
      base.h = Math.max(0.01, evalExpr(p.h, v));
      base.axis = p.axis ?? 'z';
    } else {
      base.section = p.section;
      base.length = Math.max(0.01, evalExpr(p.length, v));
      base.axis = p.axis ?? 'x';
      base.bounds = sectionBounds(resolveSection(p.section));
    }
    return base;
  });

  const ports = expand(comp.ports, vars, (p, v) => ({
    id: fillTemplate(p.id, v),
    label: p.label ? fillTemplate(p.label, v) : undefined,
    pos: evalVec(p.pos, v),
    dir: p.dir ? evalVec(p.dir, v) : null,
  }));

  const bbox = boundsOf(parts.flatMap(partCorners));
  const bom = comp.bom ?? { kind: 'piece' };
  const length = bom.kind === 'cut' && bom.lengthExpr != null ? round(evalExpr(bom.lengthExpr, vars), 10) : null;
  const kit = (comp.kit ?? []).map((k) => ({ ...k, qty: evalExpr(k.qty, vars) })).filter((k) => k.qty > 0);

  const price = bom.price != null ? round(evalExpr(bom.price, vars), 100) : null;

  const fixings = expand(comp.fixings, vars, (f, v) => ({
    type: f.type ?? 'vis',
    pos: evalVec(f.pos, v),
    dir: normalize(evalVec(f.dir ?? [0, 0, 1], v)),
    d: Math.max(0.5, evalExpr(f.d ?? 4, v)),
    l: Math.max(1, evalExpr(f.l ?? 20, v)),
    ref: f.ref,
    name: f.name,
    price: f.price,
  }));

  const recesses = expand(comp.recesses, vars, (x, v) => {
    const pos = evalVec(x.pos ?? [0, 0, 0], v);
    const size = evalVec(x.size, v).map((s) => Math.max(0.1, s));
    return { name: x.name, min: pos, max: add(pos, size) };
  });

  let joint = null;
  if (comp.joint) {
    const j = comp.joint;
    joint = {
      type: j.type,
      label: j.label,
      axis: normalize(evalVec(j.axis, vars)),
      origin: evalVec(j.origin ?? [0, 0, 0], vars),
      min: evalExpr(j.min ?? 0, vars),
      max: evalExpr(j.max, vars),
    };
    if (joint.max < joint.min) [joint.min, joint.max] = [joint.max, joint.min];
  }

  return { params, parts, ports, bbox, label: fillTemplate(bom.label, vars), length, kit, price, fixings, recesses, joint };
}

// ── Placement d'une instance ────────────────────────────────────

/** Point du repère composant → monde, pour une instance. */
export function toWorld(inst, p) {
  return add(apply(rotMatrix(inst.rot), p), inst.pos);
}

/** Boîte englobante monde (alignée sur les axes) d'une instance résolue. */
export function worldBBox(inst, resolved) {
  return boundsOf(corners([resolved.bbox.min, resolved.bbox.max]).map((c) => toWorld(inst, c)));
}

/** Ports d'une instance en coordonnées monde. */
export function worldPorts(inst, resolved) {
  const m = rotMatrix(inst.rot);
  return resolved.ports.map((p) => ({
    id: p.id,
    label: p.label,
    pos: add(apply(m, p.pos), inst.pos).map((v) => round(v)),
    dir: p.dir ? apply(m, p.dir).map((v) => round(v)) : null,
  }));
}

/** Position à donner à une instance (rotation `rot`) pour que son port local
 *  `localPort` tombe exactement sur le point monde `target`. */
export function poseOnPort(rot, localPort, target) {
  return sub(target, apply(rotMatrix(rot), localPort)).map((v) => round(v));
}

/** Union de boîtes. */
export function unionBBox(boxes) {
  if (!boxes.length) return null;
  return boundsOf(boxes.flatMap((b) => [b.min, b.max]));
}
